<script setup lang="ts">
import CalendarIcon from '@/assets/icons/CalendarIcon.vue';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import type { IRequiredActionPrompt } from '@/composables/requiredAction/requiredAction.types';
import ComposerChip from '@/core/elements/ComposerChip.vue';
import RequiredActionTarget from '@/core/elements/RequiredActionTarget.vue';
import StudentSelection from '@/core/selects/StudentSelection.vue';
import type { TStudentLike } from '@/interfaces';
import { getDateForObservationTimeline } from '@/utils/date.utils';
import { computed, useTemplateRef } from 'vue';

const emit = defineEmits<{
  close: [];
  'hide:students': [];
  'select:all-eligible-students': [];
  'show:students': [anchor: HTMLElement];
  'toggle:date': [anchor: HTMLElement];
  'update:students': [students: TStudentLike[]];
  'interact:students': [];
}>();

const props = withDefaults(
  defineProps<{
    allEligibleStudentsSelected: boolean;
    eligibleStudents: TStudentLike[];
    selectedStudents: TStudentLike[];
    date: string;
    datePopoverOpen: boolean;
    disabled?: boolean;
    mobileDrawer?: boolean;
    studentSelectionReadOnly?: boolean;
    /** Blocks closing while an entry is being saved, without locking the context chips. */
    closeDisabled?: boolean;
    showClose?: boolean;
    showDate?: boolean;
    studentsPrompt?: IRequiredActionPrompt | null;
  }>(),
  {
    disabled: false,
    mobileDrawer: false,
    studentSelectionReadOnly: false,
    closeDisabled: false,
    showClose: false,
    showDate: true,
    studentsPrompt: null,
  },
);

const dateLabel = computed(() => getDateForObservationTimeline(props.date));
const studentsSelectionRef = useTemplateRef('studentsSelectionRef');

const showStudents = async (): Promise<void> => studentsSelectionRef.value?.show();
const hideStudents = (): void => studentsSelectionRef.value?.hide();
const isStudentsOpen = computed<boolean>(() => !!studentsSelectionRef.value?.isOpen);

defineExpose({ showStudents, hideStudents, isStudentsOpen });
</script>

<template>
  <div class="flex flex-wrap items-center justify-between gap-md motion-safe:animate-composer-context-in">
    <RequiredActionTarget :prompt="props.studentsPrompt" @dismiss="emit('interact:students')">
      <template #default="{ targetClass, describedBy }">
        <StudentSelection
          ref="studentsSelectionRef"
          :class="targetClass"
          :aria-describedby="describedBy"
          :all-eligible-students-selected="props.allEligibleStudentsSelected"
          :eligible-students="props.eligibleStudents"
          :selected-students="props.selectedStudents"
          :disabled="props.disabled"
          :mobile-drawer="props.mobileDrawer"
          :read-only="props.studentSelectionReadOnly"
          @hide="emit('hide:students')"
          @select:all-eligible-students="emit('select:all-eligible-students')"
          @show="emit('show:students', $event)"
          @update:students="emit('update:students', $event)"
        />
      </template>
    </RequiredActionTarget>

    <div v-if="props.showDate || props.showClose" class="flex items-center gap-md">
      <ComposerChip
        v-if="props.showDate"
        :label="dateLabel"
        :disabled="props.disabled"
        chevron
        :expanded="props.datePopoverOpen"
        :accessible-label="`${$t('date.select')}: ${dateLabel}`"
        @click="emit('toggle:date', $event)"
      >
        <template #icon><CalendarIcon class="size-3 shrink-0" /></template>
      </ComposerChip>

      <span
        v-if="props.showDate && props.showClose"
        class="h-icon-sm shrink-0 border-l border-divider"
        aria-hidden="true"
      />

      <button
        v-if="props.showClose"
        type="button"
        class="flex size-icon-md shrink-0 items-center justify-center rounded-button text-secondary state-effects disabled:pointer-events-none disabled:opacity-40"
        :disabled="props.disabled || props.closeDisabled"
        :aria-label="$t('general.close')"
        @click="emit('close')"
      >
        <CloseIcon class="size-icon-sm" />
      </button>
    </div>
  </div>
</template>
